<template>
    <div class="autocomplete-parent">
        <input
            id="location"
            v-bind="$attrs"
            :class="classes"
            :disabled="disabled || states.geolocating"
            :value="text"
            type="text"
            autocomplete="off"
            @input="onInput"
            @blur="hideDropdown"
            @keydown.enter="selectOption"
            @keydown.down.prevent="selectNext"
            @keydown.up.prevent="selectPrevious"
        >
        <a v-if="geolocate && canGeolocate" href="#" @click.prevent="geolocateLocation">
            <i v-if="states.geolocating" class="fa fa-spinner fa-spin" />
            <i v-else class="fa fa-crosshairs" />
        </a>
        <div class="autocomplete-backdrop" />
        <div v-if="states.visible" class="autocomplete-results">
            <ul>
                <li v-for="(result, index) in results" :key="result.id">
                    <a
                        :href="result.link || '#'"
                        :class="{ highlighted: selected === index }"
                        @click.prevent="setSelectedOption($event, index)"
                    >
                        <div class="result">
                            <div class="icon">
                                <img v-if="$isWhitelabel" src="~images/autocomplete/location@2x.png" alt="Whitelabel Result">
                                <img v-else-if="result.link" src="/images/branding/p-icon.svg" alt="YPS Location Result">
                                <img v-else src="~images/autocomplete/location@2x.png" alt="Google Places Result">
                            </div>
                            <div class="text">
                                <div class="result-title">
                                    <strong v-if="result.link">{{ result.title }}</strong>
                                    <div v-else v-html="highlightText(result.title)" />
                                </div>
                                <div class="result-subtitle">{{ result.subtitle }}</div>
                            </div>
                        </div>
                    </a>
                </li>
                <li v-if="states.loading">
                    <div class="result">
                        <div class="icon">
                            <i class="fa fa-circle-o-notch fa-spin"/>
                        </div>
                        <div class="text">
                            <div class="result-title">
                                Searching&hellip;
                            </div>
                        </div>
                    </div>
                </li>
                <li v-else-if="text.length < minlength">
                    <div class="result">
                        <div class="icon">
                            <img v-if="$isWhitelabel" src="~images/autocomplete/location@2x.png" alt="Whitelabel Result">
                            <img v-else src="/images/branding/p-icon.svg" alt="YPS Location Result">
                        </div>
                        <div class="text">
                            <div class="result-title">
                                <strong>Increase search term.</strong>
                            </div>
                            <div class="result-subtitle"><small>Please enter a search longer than {{ minlength }} characters.</small></div>
                        </div>
                    </div>
                </li>
                <li v-else-if="!results.length">
                    <div class="result">
                        <div class="icon">
                            <img v-if="$isWhitelabel" src="~images/autocomplete/location@2x.png" alt="Whitelabel Result">
                            <img v-else src="/images/branding/p-icon.svg" alt="YPS Location Result">
                        </div>
                        <div class="text">
                            <div class="result-title">
                                <strong>No results found.</strong>
                            </div>
                            <div class="result-subtitle">Please adjust your search terms.</div>
                        </div>
                    </div>
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
    import throttle from 'lodash/throttle';
    import SpaceRepository from '../../repositories/SpaceRepository';
    import { googleLocationSearchPerformedEvent } from '../../modules/google-analytics';
    import storeFormSync from '../../store/form-sync';
    import axios from 'axios';

    export default {
        name: 'address-lookup',

        props: {
            classes: {
                type: [Array, Object, String],
                default() {
                    return '';
                },
            },

            value: {
                type: String,
                default() {
                    return '';
                },
            },

            disabled: {
                type: Boolean,
                default() {
                    return false;
                },
            },

            preventSubmit: {
                type: Boolean,
                default() {
                    return false;
                },
            },

            geolocate: {
                type: Boolean,
                default() {
                    return false;
                },
            },

            minlength: {
                type: [Number, String],
                default() {
                    return 4;
                },
            },
        },

        data() {
            return {
                text: '',
                results: [],
                selected: null,
                states: {
                    visible: false,
                    loading: false,
                    geolocating: false,
                },
                locatedUser: false,
            };
        },

        computed: {
            ...storeFormSync([
                'search.data.location',
                'search.data.rental',
                'search.data.start_date',
                'search.data.start_time',
                'search.data.end_date',
                'search.data.end_time',
            ]),

            selectedResult() {
                return this.results[this.selected];
            },

            canGeolocate() {
                return navigator.geolocation;
            },
        },

        watch: {
            value(val) {
                this.text = val;
            },

            selected(value) {
                if (value < 0) {
                    return value = null;
                }

                if (this.selectedResult) {
                    this.text = this.selectedResult.title;
                }
            },
        },

        created() {
            // site id has to contain only numbers and needs to be at least 4 characters long
            this.siteIdPattern = /^[\d]{4,}$/;
        },

        mounted() {
            if (this.value) {
                this.text = this.value;
            }
            this.$bus.$on('selectFirstOption',() => this.selectOption());
        },

        methods: {
            /**
             * Hide the dropdown element
             */
            hideDropdown() {
                setTimeout(() => {
                    this.states.visible = false;
                }, 150);
            },

            /**
             * Show the dropdown element
             */
            showDropdown() {
                this.states.visible = true;
            },

            /**
             * Set the selected option when a user clicks on one
             *
             * @param {MouseEvent} $event - the click event
             * @param {Number} option - the user's chosen option
             */
            setSelectedOption($event, option) {
                this.selected = option;
                this.selectOption($event);
            },

            /**
             * Select the current option
             *
             * @param {MouseEvent|undefined} e
             *
             * @returns {undefined}
             */
            selectOption(e = undefined) {
                this.states.visible = false;

                this.hideDropdown();

                if (!this.selectedResult) {
                    this.selected = 0;
                }

                if (this.selectedResult.link) {
                    this.logGoogleLocationIDSearchEvents();
                    window.location = `${ this.selectedResult.link }?payByPhone=true`;
                    return;
                }

                // If it has a link, it's one of our own results
                if (e && this.selectedResult.link) {
                    e.stopPropagation();

                    // Retain functionality of cmd+click to open new tab
                    if (!e.shiftKey && !e.ctrlKey && !e.metaKey) {
                        e.preventDefault();
                        window.location = this.selectedResult.link;
                    }

                    return;
                }

                // Stop the click from navigating
                if (e) {
                    e.preventDefault();
                }

                // Let parent components know that we've update the value
                this.$emit('input', this.text = this.selectedResult.title);

                // Tell the parent component that the place has changed
                this.$emit('changed', this.selectedResult);

                if (this.$parent.submitForm !== undefined && this.$parent.submitForm) {
                    if (this.$parent.shouldAutoSearch) {
                        return this.$parent.submitForm();
                    }

                    return;
                }

                if (!this.preventSubmit) {
                    setTimeout(() => {
                        $(this.$el).closest('form').submit();
                    }, 100);
                }
            },

            /**
             * Perform the search combining both google and site id queries
             */
            search: throttle(function() {
                this.showDropdown();
                this.selected = null;

                if (!this.text || this.text.length < this.minlength) {
                    return this.results = [];
                }

                this.states.loading = true;

                Promise.all([
                    this.searchViaId(),
                    this.searchViaGeolocate(),
                ]).then((response) => {
                    this.states.loading = false;
                    this.results = response[0].concat(response[1]);
                    this.$store.commit('search/setResults', this.results);
                }).catch(() => {
                    this.states.loading = false;
                });
            }, 1000),

            /**
             * We cannot use v-model since, since on Android the value won't be updated,
             * until compositionend fires, which kills autocomplete behaviour.
             * @param {Event} e
             */
            onInput(e) {
                this.text = e.target.value;
                this.search();
            },

            /**
             * Use geolocation to work out where the user is
             */
            geolocateLocation() {
                this.states.geolocating = true;
                navigator.geolocation.getCurrentPosition((position) => {
                    this.$emit('geolocated', position);
                    this.states.geolocating = false;
                    this.locatedUser = true;
                }, () => {
                    this.$emit('geolocation-failed');
                    this.states.geolocating = false;
                    this.locatedUser = false;
                });
            },

            /**
             * Geolocate a provided address using the Mapbox service.
             *
             * @returns {Promise}
             */
            searchViaGeolocate() {
                return axios.get(
                    `${ this.$getViewData('env.MAPBOX_API_URL') }/geocoding/v5/mapbox.places/${ encodeURIComponent(this.text) }.json`,
                    {
                        params: new URLSearchParams({
                            access_token: this.$getViewData('env.MAPBOX_API_KEY'),
                            country: 'gb',
                            // Add all existing types because of POI. POI is excluded by default.
                            types: [
                                'country',
                                'region',
                                'place',
                                'district',
                                'locality',
                                'postcode',
                                'neighborhood',
                                'address',
                            ].join(','),
                        }),
                    }
                ).then(({ data }) =>
                    data.features.map((place) => ({
                        id: place.id,
                        title: place.text,
                        subtitle: this.formatPlaceName(place),
                        lat: place.geometry.coordinates[1],
                        lng: place.geometry.coordinates[0],
                        postcode: place.place_type[0] === 'address' ? place.context[0].text : '',
                    }))
                );
            },

            /**
             * Format the place name to remove redundant
             * parts such as county and country to make
             * it look a bit more "pretty".
             *
             * @param {object} place
             *
             * @returns {string}
             */
            formatPlaceName(place) {
                if (place.id.startsWith('postal_code')) {
                    return `${ place.text }, ${ this.formatCityName(place) }`;
                }

                return [
                    'country',
                    'region',
                ].reduce((placeName, key) => {
                    const text = place.context
                        .find(({ id }) => id.startsWith(key))
                        .text;

                    if (text) {
                        return placeName
                            .replace(`${ text }, `, '')
                            .replace(`, ${ text }`, '');
                    }

                    return placeName;
                }, place.place_name.replace(`${ place.text }, `, ''));
            },

            /**
             * Search via space IDs
             *
             * @returns {Promise}
             */
            searchViaId() {
                // if the input doesn't look like a site ID, we will just return some empty results
                if (!this.siteIdPattern.test(this.text.trim())) {
                    return Promise.resolve([]);
                }

                const trimmedText = this.text.trim();

                return SpaceRepository.searchViaId({
                    location: trimmedText,
                }).then(({ data }) => {
                    return data.data.map(({ title, id, site_id }) => ({
                        id,
                        title,
                        subtitle: `Location id: ${ site_id }`,
                        link: this.$laroute.route('space.show', { spaceId: id }),
                    }));
                });
            },

            /**
             * Highlight the text based on matches against the search text
             *
             * @param {String} text
             *
             * @returns {String}
             */
            highlightText(text) {
                const regexpTerm = this.text.replace(/(?=[() ])/g, '\\'); // Escape parenthesis

                return text.replace(new RegExp(`(${ regexpTerm })`, 'ig'), '<strong>$1</strong>');
            },

            /**
             * Select the next result in the dropdown
             */
            selectNext() {
                let selected = this.selected;
                if (this.selected === null) {
                    selected--;
                }
                selected++;
                if (selected > this.results.length - 1) {
                    selected = 0;
                }

                this.selected = selected;
            },

            /**
             * Select the previous result in the dropdown
             */
            selectPrevious() {
                let selected = this.selected - 1;

                if (selected < 0) {
                    selected = this.results.length - 1;
                }

                this.selected = selected;
            },

            logGoogleLocationIDSearchEvents() {
                googleLocationSearchPerformedEvent({
                    type: 'location_id',
                    term: this.rental,
                    startDateTime: `${ this.start_date } ${ this.start_time }`,
                    endDateTime: `${ this.end_date } ${ this.end_time }`,
                    searchTerm: this.text,
                });
            },

            /**
             * Format the city name.
             *
             * @param {*} place
             *
             * @returns {string}
             */
            formatCityName(place) {
                if (place.id.startsWith('postal_code')) {
                    const address = place.place_name_en.split(', ');
                    return address[1];
                }

                const keys = ['locality', 'place', 'district'];

                const names = keys.map((key) => {
                    return place.context
                        .find(({ id }) => {
                            if (id.startsWith(key) && id.startsWith(key).text) {
                                return id.startsWith(key).text;
                            }
                        });
                });

                const first = names.find(Boolean);

                return first;
            },
        },
    };
</script>

<style lang="less">
    @import (reference) "~assets/less/variables";

    .autocomplete-parent {
        position: relative;
        > a {
            position: absolute;
            top: 0;
            right: 0;
            font-size: 20px;
            padding: 10px;
            transition: 0.15s;
            opacity: 0.25;
            text-decoration: none;
            &:hover {
                opacity: 1;
            }
            i {
                display: block;
            }
        }
        .whitelabel-search {
            font-size: 16px;
            display: block;
            line-height: 2.2em;
            font-weight: 600;
            padding-left: 30px;
            margin-top: 14px;
            min-height: 50px;
        }
    }
    .autocomplete-results {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        box-shadow: 0 4px 5px rgba(0, 0, 0, 0.08);
        border: 1px solid #c8d5e3;
        background-color: #ffffff;
        text-align: left;
        color: #3b3e41;
        font-family: @headings-font-family;
        font-size: 15px;
        font-weight: 500;
        z-index: 5;
        padding-bottom: 37px;
        ul {
            list-style: none;
            margin: 0;
            padding: 0;
            max-height: 50vh;
            overflow: auto;
            overflow-y: scroll;
            -webkit-overflow-scrolling: touch;
        }
        li {
            border-top: 1px solid #e8e8e8;
            margin: 0;
            padding: 0;
            &:first-child {
                border-top: 0;
            }
        }
        a {
            display: block;
            color: inherit;
            text-decoration: none;
            &.highlighted,
            &:hover {
                background-color: rgba(0, 0, 0, 0.15);
            }
        }
        .subtitle {
            font-size: 0.8em;
        }
        img {
            height: 23px;
        }
        .result {
            display: flex;
            width: 100%;
            align-items: center;
            > * {
                padding: 10px 0;
            }
            .text {
                padding-right: 10px;
            }
        }
        .icon {
            width: 51px;
            text-align: center;
            flex-shrink: 0;
        }
    }
    .google-footer {
        color: #32373c;
        font-size: 13px;
        font-weight: 400;
        text-align: right;
        padding: 8px 11px;
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        p {
            margin: 0;
        }
        svg {
            height: 20px;
            display: inline-block;
            vertical-align: middle;
        }
    }
</style>
